"use client";

import { ArrowUpRight, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MaintenanceHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.18
  });
  return <section data-section-id="1708" id="maintenancehero" className="relative overflow-hidden bg-background py-24 md:py-44" style={{
    borderBottom: "1px solid var(--border)"
  }}>
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")"
    }} />

      <div ref={ref} className="container relative mx-auto max-w-[1320px] px-5 md:px-8 lg:px-14">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10 items-center">
          {}
          <div className="relative lg:col-span-7 transition-all duration-700" style={{
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            {}
            <div aria-hidden="true" className="absolute -bottom-6 -right-2 h-[88%] w-[78%] opacity-[0.12]" style={{
            background: "hsl(225 73% 57%)",
            borderRadius: "62% 38% 54% 46% / 48% 56% 44% 52%",
            transform: "translate(24px, 24px) rotate(-6deg)"
          }} />

            <div className="relative overflow-hidden shadow-lg" style={{
            borderRadius: "18px 28px 64px 18px",
            clipPath: "path('M 40 0 C 180 6 360 0 520 12 C 600 18 612 80 608 180 C 604 300 612 420 600 520 C 588 600 480 612 320 608 C 180 604 60 612 24 588 C 0 540 6 360 0 200 C 0 80 8 12 40 0 Z')"
          }}>
              <div className="aspect-[5/6] w-full bg-cover bg-center" style={{
              backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/air_conditioning_service.webp)"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/air_conditioning_service.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/air_conditioning_service.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/air_conditioning_service.webp-wvc-sizes" alt="Professional technician performing air conditioning maintenance on a commercial unit" loading="lazy" decoding="async" className="h-full w-full object-cover opacity-0 max-w-full" />
              </div>
            </div>

            {}
            
          </div>

          {}
          <div className="relative lg:col-span-5">
            {}
            <div className="flex items-center gap-3 transition-all duration-700" style={{
            transitionDelay: "80ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <span className="text-muted-foreground" style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.75rem",
              letterSpacing: "0.18em",
              textTransform: "uppercase"
            }}>
                № 01 / Climate Control
              </span>
              
            </div>

            {}
            <h1 className="mt-6 font-default text-foreground transition-all duration-700" style={{
            fontSize: "clamp(2.5rem, 5.6vw, 4.75rem)",
            fontWeight: 600,
            letterSpacing: "-0.025em",
            lineHeight: 1.04,
            transitionDelay: "160ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              Expert air conditioning and climate control{" "}
              <span className="relative inline-block">
                <span style={{
                fontFamily: "var(--font-sans)",
                fontStyle: "italic",
                fontSize: "1.12em",
                color: "hsl(225 73% 57%)",
                fontWeight: 600
              }}>
                  solutions
                </span>
                {}
                <svg aria-hidden="true" viewBox="0 0 320 120" className="pointer-events-none absolute -left-4 -top-3 h-[120%] w-[115%]" preserveAspectRatio="none">
                  <path d="M 30 70 C 10 30, 80 8, 170 12 C 270 16, 312 38, 304 72 C 296 104, 220 112, 140 108 C 60 104, 8 92, 20 60" fill="none" stroke="hsl(225 73% 57%)" strokeWidth="2" strokeLinecap="round" style={{
                  strokeDasharray: 900,
                  strokeDashoffset: isVisible ? 0 : 900,
                  transition: "stroke-dashoffset 900ms ease-out 1100ms"
                }} />
                </svg>
              </span>
              .
            </h1>

            {}
            <p className="mt-7 max-w-xl transition-all duration-700" style={{
            fontSize: "clamp(1.0625rem, 1.1vw, 1.2rem)",
            lineHeight: 1.62,
            color: "hsl(215 16% 47%)",
            transitionDelay: "260ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              Coastline Air Conditioning Services provides premium air conditioning installation and maintenance for commercial and domestic properties across Sussex and Surrey, ensuring your environment remains perfectly climate-controlled.
            </p>

            {}
            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center transition-all duration-700" style={{
            transitionDelay: "340ms",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
              <Button asChild size="lg" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/85 px-7 py-6 text-base font-medium shadow-sm" style={{
              borderRadius: "9999px"
            }}>
                <Link to="/ContactUsPage">
                  Request a Quote
                  <ArrowUpRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>

              <Link to="/AirConditioningPage" className="group relative inline-flex items-center text-foreground" style={{
              fontFamily: "var(--font-sans)",
              fontSize: "1rem",
              fontWeight: 500,
              color: "hsl(225 70% 40%)"
            }}>
                
                <ArrowUpRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>

            {}
            
          </div>
        </div>
      </div>
    </section>;
}